import { ReloadOutlined } from '@ant-design/icons';
import type { StockRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatQty, parseApiTime } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

/** 效期临期阈值：与库存预警口径保持一致（90 天）。 */
const EXPIRY_WARN_DAYS = 90;

/**
 * 科室二级库（SPD）现存量（docs/01 §5 R6）：
 * 只列 `whKind=INNER_SUB` 的仓库，按批次/效期展示可占用量；消耗确认在独立页面完成。
 */
export default function SubstockList() {
  const navigate = useNavigate();
  const [warehouseId, setWarehouseId] = useState<string>('');

  const warehouses = useQuery({
    queryKey: ['substock', 'warehouses'],
    queryFn: () => hq.orgStructure.warehouse.list({ whKind: 'INNER_SUB', status: 'ACTIVE' }),
    staleTime: 5 * 60 * 1000
  });

  const options = useMemo(
    () =>
      (warehouses.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.whName}${row.deptName ? `（${row.deptName}）` : ''}`
      })),
    [warehouses.data]
  );

  useEffect(() => {
    if (!warehouseId && options.length > 0) setWarehouseId(options[0]!.value);
  }, [options, warehouseId]);

  const stock = useQuery({
    queryKey: ['substock', 'stock', warehouseId],
    enabled: Boolean(warehouseId),
    placeholderData: keepPreviousData,
    queryFn: () => hq.stock.list({ warehouseId, size: 200 })
  });

  const rows = stock.data?.items ?? [];
  const totalAmount = rows.reduce((sum, row) => sum + row.totalAmount, 0);
  // 接口按 size=200 单页返回且无续拉：达到上限时统计不完整，必须显式提示，不能静默少计
  const truncated = rows.length >= 200;

  const columns: ColumnsType<StockRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', width: 200, ellipsis: true, render: (value: string | null) => value || '-' },
    { title: '规格', dataIndex: 'spec', width: 150, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    { title: '批次', dataIndex: 'batchNo', width: 130, render: (value: string | null) => value || '-' },
    {
      title: '效期',
      dataIndex: 'expiryDate',
      width: 150,
      render: (value: string | null) => {
        const date = parseApiTime(value ?? undefined);
        if (!date) return '-';
        const days = dayjs(date).diff(dayjs(), 'day');
        const text = formatDisplayDate(value);
        if (days < 0) return <Tag color="red">{text}（已过期）</Tag>;
        if (days <= EXPIRY_WARN_DAYS) return <Tag color="orange">{text}（{days} 天）</Tag>;
        return text;
      }
    },
    { title: '库存', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '可用', dataIndex: 'availableQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '预占', dataIndex: 'reservedQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '呆滞天数', dataIndex: 'idleDays', width: 100, align: 'right', render: (value: number) => (value > 0 ? `${value} 天` : '-') }
  ];

  return (
    <Card
      className="page-card"
      title="科室小库（二级库）"
      extra={
        <Space>
          <Select
            value={warehouseId || undefined}
            options={options}
            loading={warehouses.isPending}
            placeholder="选择二级库"
            style={{ width: 260 }}
            onChange={(value) => setWarehouseId(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => stock.refetch()} loading={stock.isFetching}>
            刷新
          </Button>
          <Button type="primary" onClick={() => navigate('/substock/consume')}>
            消耗确认
          </Button>
        </Space>
      }
    >
      {warehouses.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="二级库加载失败"
          description={warehouses.error instanceof ApiError ? warehouses.error.friendlyMessage : String(warehouses.error)}
        />
      ) : null}
      {options.length === 0 && !warehouses.isPending ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前机构未配置科室二级库"
          description="请在运营端「仓库与科室」把仓库类型设为 INNER_SUB 并挂载科室。"
        />
      ) : null}
      {stock.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="库存加载失败"
          description={stock.error instanceof ApiError ? stock.error.friendlyMessage : String(stock.error)}
        />
      ) : null}

      <Space style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">
          共 {rows.length} 个批次{truncated ? '（已达 200 条上限，统计不完整）' : ''}
        </Typography.Text>
        <Typography.Text type="secondary">
          金额合计 {formatAmount(totalAmount)}{truncated ? '（不完整）' : ''}
        </Typography.Text>
      </Space>

      <Table<StockRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={Boolean(warehouseId) && stock.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: stock.isError ? '加载失败，请重试' : '该二级库暂无库存' }}
      />
    </Card>
  );
}
